/**
 * 岡山県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 * 8品が農水省「うちの郷土料理」岡山県から、4品が岡山県観光連盟の公式ページから。
 *
 * 果物について。
 *  岡山は果物が強いが、レベル1から4を果物で埋めると絵が単調になる。
 *  白桃とマスカットはレベル5・6に置き、低いレベルは菓子・小魚・野菜で組んである。
 *
 * 形のばらつき。
 *  丸い（きびだんご・清水白桃・たこめし・デミカツ丼）／細長い（ままかり・黄ニラ・手延そうめん）／
 *  棒を結んだ形（ゆべし）／縦に長い房（マスカット）／
 *  平たい（ホルモンうどん・カキオコ・祭りずし）。
 *
 * 他県との重なりをどう避けたか。
 *  - 祭りずしは、千葉の太巻き祭りずし（巻いた断面）、長崎の大村寿司・佐賀の須古寿し（押して四角く切る）と
 *    区別するため、巻かない・切らない・押さない「広く浅い一面」として描く。
 *  - カキオコは広島お好み焼きと区別するため、重ねない一枚の平たい円盤として描く。
 *  - ホルモンうどんは香川のうどん群と区別するため、汁ではなく鉄板の焼きうどんとして描く。
 *  - 千屋牛・ジャージー牛の肉料理は使わない（牛は他6県で使用済み）。蒜山は景色側で扱う。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 後楽園の「日本三名園」は使わない。香川の栗林公園で同じ判断をしている。
 *    使えるのは1952年の特別名勝指定という文化財としての事実。
 *  - 「日本のエーゲ海」（牛窓）は公式でも伝聞形。使わない。
 *  - 「日本一のだがし売場」「全国生産量第1位」は商標的呼称、または年で変わる。使わない。
 *  - 桃太郎は民話・温羅伝説の範囲にとどめ、特定の出版物やキャラクターデザインに触れない。
 *  - 大原美術館の所蔵作品名は列挙しない（著作権）。
 *  - カキオコを広島風お好み焼きと比べる文言は書かない。形の違いは絵だけで示す。
 *  - 店舗数・出荷時期・入園料など、年で変わる数字は書かない。
 *
 * 本番画像は public/assets/foods/okayama/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：黄ニラのおひたし・ゆべし・清水白桃・手延べそうめん（店で食べられる品に差し替え）
 * - えびめし：発祥の店・考案者・発祥の年は書かない（公式ページに記述がなく、店名を出すことにもなる）
 * - えびめし：具（えび・錦糸卵・グリーンピース）は書かない（公式ページでは具の記述を確かめられなかった。品名から「えび」が入るのは明らか）
 * - ひるぜん焼そば：考案した食堂の名前は書かない（店名の宣伝になる。出典には店名がある）
 * - ひるぜん焼そば：好いとん会の結成年は書かない（開いた公式ページで確かめられなかった）
 * - 岡山フルーツパフェ：値段・参加店名は書かない（すぐ変わる情報で、店の宣伝になる）
 * - 岡山フルーツパフェ：「果物王国」は見出しにしない（公式ページの語として確かめていない）
 * - 「祭りずし」を「ばら寿司（祭りずし）」に改め、絵と説明を描き直した（同じ料理なので ID は matsuri-zushi のまま）。
 * - ばら寿司（祭りずし）：藩主の名は書かない（農水省ページの表記「池田光正」と一般の表記「池田光政」が食い違うため）
 * - ばら寿司（祭りずし）：駅弁の商品名は書かない（商品の宣伝になる）
 * - 千屋牛：「日本最古の蔓牛」「和牛の中の和牛」「全国の和牛のルーツ」は言い切らない（振興会が自ら書いている大きな主張。tip は「最も古いとされる」の伝聞形にとどめた。それも外すなら tip を差し替える）
 * - 千屋牛：太田辰五郎の言葉の引用は書かない（長い）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { okayamaSceneryTour } from './okayamaScenes';
import { OKAYAMA_FOOD_HULLS } from './okayamaFoodShapes.generated';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'kibidango',
    level: 1,
    name: 'きびだんご',
    shortName: 'きびだんご',
    image: '/assets/foods/okayama/01_kibidango.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '吉備の名を宿す、手のひらのひと口。',
    source: 'https://www.okayama-kanko.jp/feature/momotarou',
    icon: { label: 'きびだんご', shape: 'mochi', baseColor: '#f4f0e4', accentColor: '#d8c088' },
    reward: {
      unlockScore: 200,
      area: '岡山市',
      headline: '岡山の旧国名「吉備」の名をもつ、手のひらにのる小さな餅菓子。',
      tip: '江戸時代には吉備津神社の門前で売られていた記録が残る。いまも岡山を代表する銘菓としてつくられている。',
      links: searchLinks('岡山市', 'きびだんご'),
    },
  },
  {
    id: 'mamakari',
    level: 2,
    name: 'ままかりの酢漬け',
    shortName: 'ままかり',
    image: '/assets/foods/okayama/02_mamakari.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '酢が骨まで通る、瀬戸内の小魚。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/41_3_okayama.html',
    icon: { label: 'ままかり', shape: 'fish', baseColor: '#c8ccd4', accentColor: '#3a5a7a' },
    reward: {
      unlockScore: 500,
      area: '瀬戸内海沿岸（岡山市ほか）',
      headline: '関東でサッパと呼ばれる小魚を酢に漬けた、岡山の祭りや祝いごとの一品。',
      tip: '旬は10月ごろ。骨は硬いものの、酢に漬かるほどやわらかくなり、尾びれまで食べられる。',
      links: searchLinks('岡山市', 'ままかり'),
    },
  },
  {
    id: 'ebimeshi',
    level: 3,
    name: 'えびめし',
    shortName: 'えびめし',
    image: '/assets/foods/okayama/03_ebimeshi.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'カラメルソースとケチャップ、カレー粉などを合わせ、スパイスをきかせたソースで炒めた焼飯。見た目は黒く辛そうだが、味は甘辛く香ばしく、まろやか。岡山の洋食系のご当地料理として知られる。',
    source:
      'https://www.okayama-kanko.jp/gourmet/11953',
    icon: { label: 'えびめし', shape: 'curry-plate', baseColor: '#3b2a1e', accentColor: '#f08a5d' },
    reward: {
      unlockScore: 1000,
      area: '岡山市',
      headline: '黒いソースで炒めた焼飯',
      tip: 'ソースには店ごとにさまざまな隠し味が加えられ、店によって味が違う。岡山市のほか倉敷市・新見市・瀬戸内市などでも食べられる。',
      links: searchLinks('岡山市', 'えびめし'),
      official: [{ label: '岡山観光WEB「えびめしが食べられる岡山後楽園周辺の店」', url: 'https://www.okayama-kanko.jp/okatabi/1320/page' }],
    },
  },
  {
    id: 'muscat',
    level: 4,
    name: 'マスカット・オブ・アレキサンドリア',
    shortName: 'マスカット',
    image: '/assets/foods/okayama/04_muscat.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '温室が育てた、香り高い緑の房。',
    source: 'https://www.okayama-kanko.jp/feature/budou/top',
    icon: { label: 'マスカット', shape: 'berry', baseColor: '#c8dc88', accentColor: '#6a8a38' },
    reward: {
      unlockScore: 1800,
      area: '岡山県内（温室栽培）',
      headline: '1886年に日本で初めて栽培に成功したとされるぶどう。透きとおるエメラルド色の実が特徴。',
      tip: '岡山のぶどう栽培はおよそ130年前に始まった。高度な温室栽培技術に支えられ、上品な香りと瑞々しい果汁をもつ。',
      links: searchLinks('岡山県', 'マスカット・オブ・アレキサンドリア'),
    },
  },
  {
    id: 'hiruzen-yakisoba',
    level: 5,
    name: 'ひるぜん焼そば',
    shortName: 'ひるぜん焼そば',
    image: '/assets/foods/okayama/05_hiruzen_yakisoba.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '味噌だれで、かしわ（親鶏）やキャベツと一緒に炒めた焼そば。昭和30年代、蒜山では家ごとに調合した味噌だれでジンギスカンなどを食べるのがはやり、その味噌だれをいかして地元の食堂が焼そばにしたのが始まり。',
    source:
      'https://www.maniwa.or.jp/web/?c=spot-2&pk=3169',
    icon: { label: '焼そば', shape: 'noodle-bowl', baseColor: '#9a4a2a', accentColor: '#8cc56a' },
    reward: {
      unlockScore: 3000,
      area: '真庭市蒜山',
      headline: '家庭の味噌だれで炒めた焼そば',
      tip: '地元の店が集まって「ひるぜん焼そば好いとん会」をつくり、2011年に姫路で開かれた第6回B-1グランプリでゴールドグランプリを受けた。',
      links: searchLinks('真庭市蒜山', 'ひるぜん焼そば'),
      official: [{ label: '真庭観光WEB「ひるぜん焼そば」', url: 'https://www.maniwa.or.jp/web/?c=spot-2&pk=3169' }],
    },
  },
  {
    id: 'hormone-udon',
    level: 6,
    name: 'ホルモンうどん',
    shortName: 'ホルモンうどん',
    image: '/assets/foods/okayama/06_hormone_udon.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '牛の町が鉄板で仕上げる、焼きうどん。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/41_19_okayama.html',
    icon: { label: 'ホルモンうどん', shape: 'ramen', baseColor: '#9a5a30', accentColor: '#e8e0c8' },
    reward: {
      unlockScore: 4500,
      area: '津山市',
      headline: '牛ホルモンと野菜をうどんごと鉄板で炒め、たれをからめる津山の焼きうどん。',
      tip: '津山では705年に牛馬の市が開かれた記録があり、古くから牛馬の流通拠点だった。店ごとに自家製のたれで味を出す。',
      links: searchLinks('津山市', 'ホルモンうどん'),
    },
  },
  {
    id: 'demi-katsudon',
    level: 7,
    name: 'デミカツ丼',
    shortName: 'デミカツ丼',
    image: '/assets/foods/okayama/07_demi_katsudon.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '洋食の記憶をまとった、岡山のかつ丼。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/41_20_okayama.html',
    icon: { label: 'デミカツ丼', shape: 'curry-plate', baseColor: '#5a3620', accentColor: '#d8a040' },
    reward: {
      unlockScore: 6500,
      area: '岡山市',
      headline: '揚げたてのとんかつに濃いデミグラスソースをかける、岡山市生まれの洋風かつ丼。',
      tip: 'ご飯にキャベツを敷き、揚げたてのとんかつをのせ、ソースをかけて生卵やグリーンピースを添える。',
      links: searchLinks('岡山市', 'デミカツ丼'),
    },
  },
  {
    id: 'kakioko',
    level: 8,
    name: '日生カキオコ',
    shortName: 'カキオコ',
    image: '/assets/foods/okayama/08_kakioko.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '牡蠣をたっぷり抱いた、平たい一枚。',
    source: 'https://www.okayama-kanko.jp/feature/around-gourmet/top',
    icon: { label: 'カキオコ', shape: 'dumpling', baseColor: '#c88a48', accentColor: '#4a3020' },
    reward: {
      unlockScore: 9000,
      area: '備前市日生町',
      headline: '備前市日生に伝わる、牡蠣をふんだんに使ったお好み焼き。地元ではカキオコと呼ぶ。',
      tip: '生地にキャベツを混ぜて丸く平たく焼き、牡蠣をふんだんに加える。重ねずに一枚で仕上げるのが見た目の特徴。',
      links: searchLinks('備前市日生町', 'カキオコ'),
    },
  },
  {
    id: 'okayama-fruit-parfait',
    level: 9,
    name: '岡山フルーツパフェ',
    shortName: 'フルーツパフェ',
    image: '/assets/foods/okayama/09_okayama_fruit_parfait.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '白桃やマスカット、ピオーネなど岡山で育った果物を、アイスクリームやソースと一緒に盛るパフェ。岡山市は「フルーツパフェの街おかやま」として地元の果物を広めていて、季節によってのせる果物が変わる。',
    source:
      'https://www.okayama-kanko.jp/gourmet/11972',
    icon: { label: 'パフェ', shape: 'berry', baseColor: '#f6dcd2', accentColor: '#9bc53d' },
    reward: {
      unlockScore: 12000,
      area: '岡山市',
      headline: '季節の果物を高く盛る',
      tip: '「フルーツパフェの街おかやま」は2009年に岡山市内で始まった。農園が育てた岡山県産の果物を気軽に楽しんでもらいたい、という願いから生まれた。',
      links: searchLinks('岡山市', 'フルーツパフェ'),
      official: [{ label: 'フルーツパフェの街おかやま（実行委員会）', url: 'https://okayama-parfait.com/' }],
    },
  },
  {
    id: 'tako-meshi',
    level: 10,
    name: 'たこめし',
    shortName: 'たこめし',
    image: '/assets/foods/okayama/10_tako_meshi.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '船の上から始まった、炊き込み飯。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/41_10_okayama.html',
    icon: { label: 'たこめし', shape: 'roe', baseColor: '#e0d0b0', accentColor: '#b04a58' },
    reward: {
      unlockScore: 16000,
      area: '倉敷市（児島・下津井）',
      headline: '下津井の海で育ったたこを米とともに炊き上げる、倉敷市児島に伝わる炊き込みご飯。',
      tip: '岡山県のたこ漁獲量は全国でもトップクラス。下津井付近のたこは身がしっかりして独特の歯ごたえがある。炊き上がりにねぎを混ぜる。',
      links: searchLinks('倉敷市児島', 'たこめし'),
    },
  },
  {
    id: 'matsuri-zushi',
    level: 11,
    name: 'ばら寿司（祭りずし）',
    shortName: 'ばら寿司',
    image: '/assets/foods/okayama/11_matsuri_zushi.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: 'すし飯の上に、サワラ、アナゴ、エビ、タコ、レンコン、タケノコ、干しシイタケなど十種あまりの具をのせる。江戸時代、岡山藩主が「一汁一菜」の倹約令を出したとき、具をすしに入れればおかずではない、と工夫したのが始まりとされる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/41_1_okayama.html',
    icon: { label: 'ばら寿司', shape: 'feast', baseColor: '#f3e3b5', accentColor: '#e8784a' },
    reward: {
      unlockScore: 22000,
      area: '岡山県全域（備前地方）',
      headline: '倹約令から生まれた具だくさん',
      tip: '春秋の祭りや盆、法事などで客をもてなすハレの料理。春はサワラ・フキ・タケノコ、秋はマツタケと、季節で具が変わる。蒸して温める「ぬくずし」という食べ方もある。',
      links: searchLinks('岡山市', 'ばら寿司'),
      official: [{ label: '岡山観光WEB「岡山ばらずし」', url: 'https://www.okayama-kanko.jp/gourmet/11993' }],
    },
  },
  {
    id: 'chiya-gyu',
    level: 12,
    name: '千屋牛',
    shortName: '千屋牛',
    image: '/assets/foods/okayama/12_chiya_gyu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '新見市で育てた黒毛和種。1850年ごろ、製鉄で財を成した太田辰五郎が、砂鉄が尽きる先を見すえて竹の谷から良い牛を買い入れ、子牛を農家に預けて増やした。ほどよい霜降りと赤身の釣り合いがよい。',
    source:
      'https://chiyagyu-shinkokai.jp/about.php',
    icon: { label: '千屋牛', shape: 'feast', baseColor: '#7a2e22', accentColor: '#e9c7b5' },
    reward: {
      unlockScore: 30000,
      area: '新見市',
      headline: '製鉄の町で育てられた和牛',
      tip: '太田辰五郎の取り組みから生まれた系統の牛「竹の谷蔓（たけのたにづる）」は、日本三名蔓のうち最も古いとされ、いまの千屋牛の祖先にあたる。',
      links: searchLinks('新見市', '千屋牛'),
      official: [{ label: '千屋牛振興会', url: 'https://chiyagyu-shinkokai.jp/about.php' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const okayamaFoodStage: StageDefinition = {
  regionId: 'okayama',
  regionName: '岡山県',
  mode: 'food',
  title: '岡山めしマージ',
  theme: {
    primaryColor: '#2a4a3a',
    secondaryColor: '#d8b858',
    accentColor: '#f0d880',
    backgroundColor: '#14181a',
  },
  /** 背景ツアー「岡山めぐり」。軸は一日の時間。早朝の備中松山城から、夜の倉敷美観地区へ。 */
  scenery: {
    tour: okayamaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, OKAYAMA_FOOD_HULLS),
};
